import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["count"]
  static values = { liked: Boolean, count: Number }

  connect() {
    this.originalLiked = this.likedValue
    this.originalCount = this.countValue
  }

  // This runs when the form is about to be submitted
  turbo_submit_start() {
    this.showOptimisticUpdate()
  }

  // This runs if the server request succeeds (Turbo will replace the element)
  turbo_submit_end(event) {
    if (event.detail.success) {
      // Turbo Stream response will replace the element, so we don't need to do anything
      console.log('✅ Like action succeeded')
    } else {
      // Revert on failure
      this.revertOptimisticUpdate()
      console.log('❌ Like action failed, reverting')
    }
  }

  // This runs if there's a network error
  turbo_before_fetch_response(event) {
    if (!event.detail.fetchResponse.response.ok) {
      this.revertOptimisticUpdate()
      console.log('❌ Network error, reverting like state')
    }
  }

  showOptimisticUpdate() {
    const button = this.element
    const icon = button.querySelector('i')
    const isCurrentlyLiked = this.likedValue

    if (isCurrentlyLiked) {
      // Currently liked, so we're unliking
      icon.className = 'far fa-heart group-hover:scale-110 transition-transform duration-200'
      button.className = button.className.replace('text-red-500 hover:text-red-600', 'text-gray-400 hover:text-red-500')
      this.countTarget.textContent = this.countValue - 1
      this.likedValue = false
      this.updateSocialScore(-1) // Decrease social score
    } else {
      // Currently not liked, so we're liking
      icon.className = 'fas fa-heart group-hover:scale-110 transition-transform duration-200'
      button.className = button.className.replace('text-gray-400 hover:text-red-500', 'text-red-500 hover:text-red-600')
      this.countTarget.textContent = this.countValue + 1
      this.likedValue = true
      this.updateSocialScore(1) // Increase social score
      
      // Add a subtle bounce animation
      icon.style.transform = 'scale(1.2)'
      setTimeout(() => {
        icon.style.transform = 'scale(1)'
      }, 200)
    }

    // Add a subtle loading state
    button.style.opacity = '0.8'
    setTimeout(() => {
      button.style.opacity = '1'
    }, 300)
  }

  updateSocialScore(change) {
    // Find the social score element in the same post stats container
    const postStatsContainer = this.element.closest('[id^="post-stats-"]')
    if (!postStatsContainer) return

    const socialScoreElement = postStatsContainer.querySelector('[data-social-score]')
    if (!socialScoreElement) return

    // Parse current social score (handle 'k', 'M', 'B' suffixes)
    const currentText = socialScoreElement.textContent.trim()
    let currentScore = this.parseShortNumber(currentText)
    
    // Apply change
    const newScore = Math.max(0, currentScore + change)
    
    // Format back to short format
    socialScoreElement.textContent = this.formatShortNumber(newScore)
  }

  parseShortNumber(text) {
    const num = parseFloat(text)
    if (text.includes('k') || text.includes('K')) {
      return Math.round(num * 1000)
    } else if (text.includes('m') || text.includes('M')) {
      return Math.round(num * 1000000)
    } else if (text.includes('b') || text.includes('B')) {
      return Math.round(num * 1000000000)
    }
    return Math.round(num)
  }

  formatShortNumber(number) {
    if (number < 1000) return number.toString()
    if (number < 1000000) {
      const k = number / 1000
      return k % 1 === 0 ? `${k}k` : `${k.toFixed(1)}k`
    }
    if (number < 1000000000) {
      const m = number / 1000000
      return m % 1 === 0 ? `${m}M` : `${m.toFixed(1)}M`
    }
    const b = number / 1000000000
    return b % 1 === 0 ? `${b}B` : `${b.toFixed(1)}B`
  }

  revertOptimisticUpdate() {
    const button = this.element
    const icon = button.querySelector('i')

    // Revert to original state
    if (this.originalLiked) {
      icon.className = 'fas fa-heart group-hover:scale-110 transition-transform duration-200'
      button.className = button.className.replace('text-gray-400 hover:text-red-500', 'text-red-500 hover:text-red-600')
    } else {
      icon.className = 'far fa-heart group-hover:scale-110 transition-transform duration-200'  
      button.className = button.className.replace('text-red-500 hover:text-red-600', 'text-gray-400 hover:text-red-500')
    }
    
    this.countTarget.textContent = this.originalCount
    this.likedValue = this.originalLiked
    button.style.opacity = '1'
  }
}